import { useCallback, useEffect, useMemo, useState } from 'react';
import { showAlert } from 'utils/ErrorMessage';
import { downloadSecondarySalesOrderReport, fetchSecondarySalesOrderReport, getCustomerData, getUserDivisionData } from 'Service/ApiService';

const PAGE_SIZE = 100;
const getLocalToday = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const SecondarySalesOrderReportController = () => {
  const [divisions, setDivisions] = useState([]);
  const [division, setDivision] = useState('');
  const [dealers, setDealers] = useState([]);
  const [dealer, setDealer] = useState('');
  const [dealerSearch, setDealerSearch] = useState('');
  const [isDealerLoading, setIsDealerLoading] = useState(false);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const filters = useMemo(() => ({
    division, dealer, fromDate, toDate, status, page: page + 1, perPage: PAGE_SIZE,
  }), [division, dealer, fromDate, toDate, status, page]);

  const loadDivisions = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const result = await getUserDivisionData(user?.userCode || '');
    if (result?.status !== true || !Array.isArray(result.divisions)) throw new Error(result?.msg || 'Could not load assigned divisions.');
    if (result.divisions.length === 0) showAlert('No divisions are assigned to your account.');
    setDivisions(result.divisions.map((item) => ({ value: String(item.divCode), label: `${item.divName} (${item.divCode})` })));
    setDivision('');
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchSecondarySalesOrderReport(filters);
      if (result?.success !== true) throw new Error(result?.msg || 'Could not load report.');
      setRows(result.data || []);
      setTotal(Number(result.pagination?.totalRecords || 0));
    } catch (e) {
      setRows([]);
      setTotal(0);
      showAlert(e.message || 'Could not load report.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadDivisions().catch((e) => { showAlert(e.message || 'Could not load assigned divisions.'); setLoading(false); });
  }, [loadDivisions]);
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      if (dealerSearch.trim() && dealerSearch.trim().length < 3) { setDealers([]); return; }
      try {
        setIsDealerLoading(true);
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const result = await getCustomerData(user?.userCode || '', dealerSearch.trim());
        if (!active) return;
        const uniqueDealers = (result?.customerList || []).reduce((options, customer) => {
          const code = String(customer.partyCode || customer.customerCode || '').trim();
          if (!code || options.some((option) => option.value === code)) return options;
          const name = String(customer.partyName || customer.customerName || '-').trim();
          const city = String(customer.city || '').trim();
          options.push({ value: code, label: `${name}-${city}-${code}` });
          return options;
        }, []);
        setDealers(uniqueDealers);
      } catch (e) {
        if (active) { setDealers([]); showAlert(e.message || 'Could not load dealers.'); }
      } finally {
        if (active) setIsDealerLoading(false);
      }
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [dealerSearch]);
  useEffect(() => { loadRows(); }, [loadRows]);

  const applyFilter = (setter, value) => { setter(value); setPage(0); };
  const handleDateChange = (setter, value) => {
    if (value && value > getLocalToday()) { showAlert('Future dates are not allowed.'); return; }
    applyFilter(setter, value);
  };
  const reset = () => { setDealer(''); setFromDate(''); setToDate(''); setStatus('all'); setPage(0); };
  const exportExcel = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await downloadSecondarySalesOrderReport(filters);
    } catch (e) {
      showAlert(e.message || 'Could not export report.');
    } finally {
      setExporting(false);
    }
  };

  return {
    divisions, division, setDivision, dealers, dealer, setDealer, dealerSearch, setDealerSearch,
    isDealerLoading, fromDate, setFromDate, toDate, setToDate, status, setStatus, page, setPage,
    rows, total, loading, exporting, filters, getToday: getLocalToday(), applyFilter,
    handleDateChange, reset, exportExcel, loadRows,
  };
};

export default SecondarySalesOrderReportController;